// ReportsPage.tsx · v12.10.15 (#1627 print header uses the practice name, session fallback) · 2026-10-13 · VoiceSpectrum AI
// ReportsPage.tsx · v12.10.13 (#1564: ?tab=record&call=<id>&do=print|pdf opens Call record on that call - the message drawer's Print ▾) · 2026-10-10 · VoiceSpectrum AI
// ReportsPage.tsx · v12.10.13 (#1577 T78 / #1603 T88: the top like Messages for both tabs - REPORTS label, the tabs as pills Analyze ·
//   Call record · Productivity (greyed, SOON), then the title of the open report; data-track-screen on the tabs for the usage
//   tracker; Analyze = How the day went after Show (#1572), audited Print / PDF / HTML (#1594)) · 2026-10-10 · VoiceSpectrum AI
// ReportsPage.tsx · v12.10.12 (#1546 T80 How the day went, #1550 T81 Call record) · 2026-10-10 · VoiceSpectrum AI
// Reports (the "Reports › View" right - Security › User groups › Reports › Call activity). Tabs: How the day went (the shared
// call story, UI-PROTO-REPORTS-B1 v1.6 LOCKED) · Call record (UI-PROTO-CALL-RECORD v1.1) · Productivity (later, #1547).
import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import CallSummary, { summarySheets, type CallSummaryData, type ReportWindow } from '@shared-ui/CallSummary';
import api from '../lib/api';
import { useAccess } from '../lib/useAccess';
import { postAndSave } from '../lib/reportDownload';
import { toast } from '../lib/toast';
import CallRecordTab from './reports/CallRecordTab';
import '../styles/reports.css';
import { sessionPracticeName } from './reports/CallRecordSheet';

type Tab = 'day' | 'record';

const TITLES: Record<Tab, [string, string]> = {
  day: ['How the day went', 'How your calls went - for a day, a week or any dates'],
  record: ['Call record', 'Print, save or copy the record of one call or of a day’s calls'],
};

export default function ReportsPage() {
  const { canOpen, ready } = useAccess();
  const [params] = useSearchParams();
  const openCall = params.get('call');
  const autoDo = params.get('do') === 'pdf' ? 'pdf' : params.get('do') === 'print' ? 'print' : null;
  const [tab, setTab] = useState<Tab>(params.get('tab') === 'record' || openCall ? 'record' : 'day');
  if (ready && !canOpen('rep_calls')) {
    return <div className="rp-root vsr-root"><div className="vsr-sect">REPORTS</div><p className="vsr-sub" style={{ marginTop: 10 }}>Your user group doesn&rsquo;t include Reports - ask your practice admin (Security › User groups › Reports › Call activity).</p></div>;
  }
  return (
    <div className="rp-root vsr-root">
      <div className="vsr-sect">REPORTS</div>
      <div className="vsr-tabs" role="tablist" aria-label="Reports">
        <button type="button" role="tab" aria-selected={tab === 'day'} className={tab === 'day' ? 'on' : ''} data-track-screen="reports-analyze" onClick={() => setTab('day')}>Analyze</button>
        <button type="button" role="tab" aria-selected={tab === 'record'} className={tab === 'record' ? 'on' : ''} data-track-screen="reports-call-record" onClick={() => setTab('record')}>Call record</button>
        <button type="button" role="tab" aria-disabled="true" disabled title="Not available yet">Productivity<span className="vsr-soon">SOON</span></button>
      </div>
      <div className="vsr-hd"><div><h1>{TITLES[tab][0]}</h1><div className="vsr-sub">{TITLES[tab][1]}</div></div></div>
      {tab === 'day' ? <DayTab /> : <CallRecordTab openCall={openCall} autoDo={autoDo} />}
    </div>
  );
}

function DayTab() {
  const [shown, setShown] = useState<ReportWindow | null>(null);   // #1572: nothing loads until Show
  const org = useQuery<{ identity?: { displayName?: string; legalName?: string } }>({ queryKey: ['t2-org-name'], queryFn: () => api.get('/t2/settings/organization'), staleTime: 300_000 });
  const practice = org.data?.identity?.displayName || org.data?.identity?.legalName || sessionPracticeName() || 'Your practice';   // v12.10.15 (#1627)
  const q = useQuery<CallSummaryData>({
    queryKey: ['t2-report-summary', shown?.from, shown?.to, shown?.at],
    queryFn: () => api.get(`/t2/reports/summary?from=${encodeURIComponent(shown!.from)}&to=${encodeURIComponent(shown!.to)}`),
    enabled: !!shown,
    staleTime: 60_000,
  });
  return (
    <CallSummary
      data={shown ? q.data : undefined} loading={!!shown && q.isLoading} error={q.error ? 'The report could not be loaded. Try again.' : null}
      practiceName={practice} viewLabel="Analyze" shown={shown} onShow={setShown}
      onAudit={(action) => {
        if (!shown) return;
        void api.post('/t2/reports/summary/audit', { action, calls: q.data?.picture.calls ?? 0, from: shown.from, to: shown.to }).catch(() => { /* the API logs its own failure */ });
      }}
      onExcel={async () => {
        if (!q.data) return;
        try { await postAndSave('/t2/reports/xlsx', { title: `${practice} how the day went`, kind: 'summary', calls: q.data.picture.calls, sheets: summarySheets(q.data) }, 'how-the-day-went.xlsx'); }
        catch { toast('error', 'The Excel file was not made', 'Try again.'); }
      }}
      onShare={async (email) => {
        if (!shown) return;
        const r = await api.post<{ sent: boolean; queued: boolean }>('/t2/reports/summary/share', { from: shown.from, to: shown.to, email });
        toast('success', r.sent ? 'Summary sent' : 'Summary queued', r.sent ? `To ${email}.` : `It goes out to ${email} as soon as email is available.`);
      }}
    />
  );
}
